export const RULE_VERSION = 'frequency-30-v1';
export const LOOKBACK = 30;
export const PLAYS = {
  six: [ ['parity', '单双'], ['size', '大小'], ['family', '家野肖'] ],
  pc: [ ['parity', '单双'], ['size', '大小'], ['groups', '双组'], ['dragon', '龙虎'] ],
};
const FAMILY = new Set(['牛', '马', '羊', '鸡', '狗', '猪']);
export function classify(draw, kind, play) {
  const numbers = draw.numbers;
  if (kind === 'six') {
    const special = numbers[6];
    if (play === 'family') return draw.zodiacs?.[6] ? (FAMILY.has(draw.zodiacs[6]) ? '家' : '野') : null;
    if (special === 49) return '和';
    return play === 'parity' ? (special % 2 ? '单' : '双') : (special >= 25 ? '大' : '小');
  }
  const sum = numbers.reduce((a, b) => a + b, 0);
  const parity = sum % 2 ? '单' : '双';
  const size = sum >= 14 ? '大' : '小';
  if (play === 'groups') return size + parity;
  if (play === 'dragon') return numbers[0] === numbers[2] ? '和' : numbers[0] > numbers[2] ? '龙' : '虎';
  return play === 'parity' ? parity : size;
}
export function predict(history, kind, play) {
  if (history.length < LOOKBACK) return null;
  const sample = history.slice(0, LOOKBACK).map(draw => classify(draw, kind, play));
  // Unknown zodiac mappings invalidate this play instead of silently shrinking its sample.
  if (sample.some(v => !v)) return null;
  const counts = new Map();
  sample.filter(v => v !== '和').forEach(v => counts.set(v, (counts.get(v) || 0) + 1));
  const sorted = [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const count = play === 'groups' ? 2 : 1;
  if (sorted.length < count || (sorted[count] && sorted[count - 1][1] === sorted[count][1])) return null;
  return sorted.slice(0, count).map(([name]) => name);
}
export const COMBINATION_VERSION = 'following-combinations-v1';
export const MIN_FOLLOWING_SAMPLES = 20;
export function followingDraws(history, kind, latest = history[0]) {
  const valueOf = draw => kind === 'six' ? draw.numbers[6] : draw.numbers.reduce((a,b)=>a+b,0);
  if (!latest) return [];
  return history.slice(1).flatMap((draw,index) =>
    valueOf(draw)===valueOf(latest) && Number(history[index].issue)-Number(draw.issue)===1 ? [history[index]] : []);
}
export function predictCombinations(history, kind, latest = history[0]) {
  const following = followingDraws(history,kind,latest);
  const counts = new Map(['大单','大双','小单','小双'].map(name=>[name,0]));
  following.forEach(draw=>{
    const group=classify(draw,kind,'size')+classify(draw,kind,'parity');
    if (counts.has(group)) counts.set(group,counts.get(group)+1);
  });
  const ranked=[...counts].sort((a,b)=>b[1]-a[1]);
  const total=[...counts.values()].reduce((a,b)=>a+b,0);
  return {total, followTotal:following.length, version:COMBINATION_VERSION,
    choices:total>=MIN_FOLLOWING_SAMPLES && ranked[1][1]>ranked[2][1] ? ranked.slice(0,2).map(([name])=>name) : null,
    excluded:total>=MIN_FOLLOWING_SAMPLES && ranked[3][1]<ranked[2][1] ? ranked[3][0] : null};
}
export function backtest(history, kind, play) {
  let hits = 0, settled = 0, skipped = 0;
  const records = [];
  for (let i = Math.min(history.length - LOOKBACK - 1, 199); i >= 0; i--) {
    const choice = predict(history.slice(i + 1), kind, play);
    const actual = classify(history[i], kind, play);
    const valid = choice && actual && actual !== '和';
    const hit = valid ? choice.includes(actual) : null;
    if (valid) { settled++; if (hit) hits++; } else skipped++;
    records.unshift({ issue: history[i].issue, choice, actual, hit });
  }
  return { hits, settled, skipped, records, rate: settled ? hits / settled : null };
}
export function numberStats(history, kind) {
  const values = history.map(d => kind === 'six' ? d.numbers[6] : d.numbers.reduce((a, b) => a + b, 0));
  return Array.from({ length: kind === 'six' ? 49 : 28 }, (_, i) => {
    const number = i + (kind === 'six' ? 1 : 0);
    const first = values.indexOf(number);
    return { number, count: values.filter(n => n === number).length, missing: first < 0 ? values.length : first, atLeast: first < 0 };
  });
}
// History is newest first; index i - 1 is the draw following index i.
export function followingNumberAnalysis(history, kind, latest = history[0]) {
  const valueOf = draw => kind === 'six' ? draw.numbers[6] : draw.numbers.reduce((a,b) => a+b,0);
  if (!latest) return { number:null, total:0, size:null, parity:null, next:null };
  const number = valueOf(latest);
  const following = followingDraws(history,kind,latest);
  const mostCommon = (values, allowTie = false) => {
    const counts = new Map();
    values.forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
    const ranked = [...counts].sort((a,b) => b[1]-a[1] || (allowTie ? a[0]-b[0] : 0));
    const tied = Boolean(ranked[1] && ranked[0][1] === ranked[1][1]);
    if (tied && !allowTie) return {value:ranked[0][0],count:ranked[0][1],tiedValues:ranked.filter(row=>row[1]===ranked[0][1]).map(row=>row[0])};
    return !ranked.length ? null : {value:ranked[0][0], count:ranked[0][1], ...(tied ? {tied:true} : {})};
  };
  return {number, total:following.length, size:mostCommon(following.map(draw=>classify(draw,kind,'size'))), parity:mostCommon(following.map(draw=>classify(draw,kind,'parity'))), next:mostCommon(following.map(valueOf),true)};
}
export function currentRun(history, kind, play) {
  const value = history[0] && classify(history[0], kind, play);
  if (!value || value === '和') return { value: '—', count: 0 };
  const end = history.findIndex(row => classify(row, kind, play) !== value);
  return { value, count: end < 0 ? history.length : end };
}
